import type { Container, Operation } from '@packages/core/container';

import { Ssm5Client } from '@packages/clients';

import type { Ssm5ConnectPhase, Ssm5ConnectResult } from '../types';

import { toConnectPhase, toConnectResult } from '../helpers/mappers';

/** Parameters of {@link ConnectToVehicle}. */
export type ConnectToVehicleParams = {
    /** The normalized Cloud Connector ID to connect through. */
    cloudConnectorId: string;
    /** Called with each progress line of the lookup: the phase it is in and the progress from 0 to 100. */
    onPhase?: (phase: Ssm5ConnectPhase, progress: number) => void;
    /** Aborts the attempt. */
    signal?: AbortSignal;
};

/**
 * Open an SSM5 session on the vehicle behind a Cloud Connector: the backend pairs with the
 * connector, reads the VIN and identifies the vehicle, streaming its progress on the way.
 */
export class ConnectToVehicle implements Operation {
    /**
     * Register this operation with the SSM5 client as its dependency.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(ConnectToVehicle, [Ssm5Client]);
    }

    /** @param client - the SSM5 API client */
    constructor(private client: Ssm5Client) {}

    /**
     * Run the lookup and settle the attempt from its last line.
     *
     * @param params - {@link ConnectToVehicleParams}
     * @returns the outcome of the attempt; rejects when the attempt is aborted or the lookup throws
     */
    async exec({ cloudConnectorId, onPhase, signal }: ConnectToVehicleParams): Promise<Ssm5ConnectResult> {
        const lastLine = await this.client.getVehicleByCloudConnectorId({ cloudConnectorId }, signal, (line) => {
            const phase = toConnectPhase(line);

            if (phase) {
                onPhase?.(phase, line.progress);
            }
        });

        return toConnectResult(lastLine, cloudConnectorId);
    }
}
